import { storage } from './storage';
import { STORAGE_KEYS, createFeatureKey } from '../constants/storageKeys';

// Feature flag definitions
export const FEATURE_FLAGS = {
  exportPrompts: false, // Premium Feature
  importPrompts: true, // Free Feature
  deleteAllPrompts: false, // Premium Feature
  cloudSync: false, // Premium Feature
  variableHistory: false, // Premium Feature
  openExternal: true, // Free Feature
  workPrompts: false, // Premium Feature
  folderColors: false, // Premium Feature
  theme: true, // Free Feature
  rememberLastFolder: false, // Premium Feature
  folderIcons: false, // Premium Feature
  telemetryEnabled: true, // Free Feature, enabled by default
  weeklyBackup: false, // Premium Feature
  fullScreenView: false, // Premium Feature
} as const;

// Type for feature names
export type FeatureName = keyof typeof FEATURE_FLAGS;

// Development mode toggle - set to false for production
export const DEVELOPMENT_MODE = false;

/**
 * Helper function to determine if a feature is a premium feature
 */
export function isPremiumFeature(feature: FeatureName): boolean {
  // Premium features are those that are disabled by default in FEATURE_FLAGS
  return !FEATURE_FLAGS[feature];
}

/**
 * Check if a feature is enabled based on user's settings and feature flags
 * During development (DEVELOPMENT_MODE = true), all features are enabled
 */
export async function checkFeatureEnabled(feature: FeatureName): Promise<boolean> {
  if (DEVELOPMENT_MODE) return true;

  try {
    // Check premium status from settings
    const settings = await storage.get(STORAGE_KEYS.SETTINGS);
    //console.log(`[checkFeatureEnabled] Settings for ${feature}:`, settings);

    // Check premium status in multiple possible locations
    let isPremium = false;

    // Check direct settings object
    if (settings?.premium_status === 'paid') {
      isPremium = true;
      //console.log('[checkFeatureEnabled] Premium detected in settings.premium_status');
    }
    // Check nested settings.settings object
    else if (settings?.settings?.premium_status === 'paid') {
      isPremium = true;
      //console.log('[checkFeatureEnabled] Premium detected in settings.settings.premium_status');
    }
    // Check data property (StorageData wrapper)
    else if (settings?.data?.premium_status === 'paid') {
      isPremium = true;
      //console.log('[checkFeatureEnabled] Premium detected in settings.data.premium_status');
    }
    // Check nested data.settings object
    else if (settings?.data?.settings?.premium_status === 'paid') {
      isPremium = true;
      //console.log('[checkFeatureEnabled] Premium detected in settings.data.settings.premium_status');
    }

    //console.log(`[checkFeatureEnabled] isPremium for ${feature}:`, isPremium);

    // Check user preference
    const rawUserPreference = await storage.get(createFeatureKey(feature));
    console.log(`[checkFeatureEnabled] User preference for ${feature}:`, rawUserPreference);

    if (rawUserPreference !== null && rawUserPreference !== undefined) {
      // The stored value may be wrapped as { data: <value> } by the storage layer;
      // unwrap it and coerce to a real boolean so we never return a truthy object.
      const unwrapped =
        rawUserPreference && typeof rawUserPreference === 'object' && 'data' in rawUserPreference
          ? (rawUserPreference as { data: unknown }).data
          : rawUserPreference;
      const userPreference = unwrapped === true || unwrapped === 'true';

      // For premium features, a stored "enabled" preference must still be gated
      // behind current premium status. Otherwise a user who downgraded (or whose
      // flag was set by the storage-change handler) keeps premium features unlocked.
      if (isPremiumFeature(feature) && userPreference && !isPremium) {
        console.log(
          `[checkFeatureEnabled] Ignoring stored preference for premium ${feature}: not premium`
        );
        return false;
      }

      console.log(`[checkFeatureEnabled] Using user preference for ${feature}:`, userPreference);
      return userPreference;
    }

    // If no user preference is set, use default based on premium status
    const result = isPremium || (FEATURE_FLAGS[feature] ?? false);
    console.log(
      `[checkFeatureEnabled] Final result for ${feature}:`,
      result,
      `(isPremium: ${isPremium}, defaultValue: ${FEATURE_FLAGS[feature] ?? false})`
    );

    return result;
  } catch (error) {
    console.error('Error checking feature status:', error);
    // In case of error, fall back to the most restrictive state
    return FEATURE_FLAGS[feature] ?? false;
  }
}

export function markPremiumFeatures(): void {
  document.querySelectorAll('.premium-feature').forEach(el => {
    if (!el.classList.contains('premium-label')) {
      el.classList.add('premium-label');
      // Only append "(Premium)" if it's not already there
      if (!el.textContent?.includes('(Premium)')) {
        el.textContent = `${el.textContent} (Premium)`;
      }
    }
  });
}
